import React, { useEffect, useRef, useState } from "react";
import { OpenSheetMusicDisplay } from "opensheetmusicdisplay";

type SheetMusicViewerProps = {
  musicXmlUrl: string | null | undefined;
};

export const SheetMusicViewer: React.FC<SheetMusicViewerProps> = ({ musicXmlUrl }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const osmdRef = useRef<OpenSheetMusicDisplay | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!musicXmlUrl) {
      // No score available; clear any previous rendering
      if (containerRef.current) {
        containerRef.current.innerHTML = "";
      }
      setLoading(false);
      setError(null);
      return;
    }

    const container = containerRef.current;
    if (!container) return;

    let cancelled = false;

    async function loadScore() {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(musicXmlUrl);
        if (!response.ok) {
          throw new Error(`Failed to load score (${response.status})`);
        }
        const xml = await response.text();
        if (cancelled) return;

        if (!xml.trim()) {
          throw new Error("Empty MusicXML response.");
        }

        if (!osmdRef.current) {
          osmdRef.current = new OpenSheetMusicDisplay(container, {
            autoResize: true,
            backend: "svg",
            drawTitle: false,
            drawSubtitle: false,
            drawCredits: false,
            drawPartNames: false,
            drawComposer: false,
            drawLyricist: false,
            drawMeasureNumbers: true,
          });
          osmdRef.current.Zoom = 1.1;
        }

        await osmdRef.current.load(xml);
        if (cancelled) return;
        await osmdRef.current.render();
        if (!cancelled) {
          setLoading(false);
        }
      } catch (e: any) {
        if (cancelled) return;
        const baseMsg = e?.message ?? "Failed to render sheet music.";
        setError(`Sheet music rendering failed. ${baseMsg}`);
        setLoading(false);
      }
    }

    loadScore();

    return () => {
      cancelled = true;
    };
  }, [musicXmlUrl]);

  if (!musicXmlUrl) {
    return (
      <div className="sheet-music-viewer sheet-music-viewer-empty">
        <p className="sheet-music-message">No score available for this transcription.</p>
      </div>
    );
  }

  return (
    <div className="sheet-music-viewer">
      <div className="sheet-music-header">
        <span>Sheet music preview</span>
        {loading && <span className="sheet-music-status">Rendering…</span>}
      </div>
      {error && <p className="sheet-music-error">{error}</p>}
      <div className="sheet-music-canvas" ref={containerRef} />
    </div>
  );
};

